import { ref } from 'vue';
import { segmentsClient } from '~/services';
import { createGlobalState } from '@vueuse/core';
import type { ClientContext } from '~/services/contextClient';
import { arraysEqual, chunkArray } from '~/utils/array';

const IDS_PER_REQUEST = 10;

export const useSegmentsContactsCount = createGlobalState(() => {
    // [segmentId]: contactsCount
    // undefined = loading
    // null = not found
    const contactsCount = ref<Record<number, number>>({});

    const fetching = ref(false);
    const lastGroupIds = ref<string[] | undefined>(undefined);

    const resetContactsCount = () => {
        contactsCount.value = {};
        fetching.value = false;
    };

    const fetchSegmentsContactsCount = async (
        context: ClientContext,
        segmentIds: number[],
        params?: {
            group_ids?: string[];
        }
    ) => {
        // Check if group_ids changed - if so, reset cache
        if (!arraysEqual(params?.group_ids, lastGroupIds.value)) {
            resetContactsCount();
            lastGroupIds.value = params?.group_ids;
        }

        // remove existing ids to avoid computing them again
        segmentIds = segmentIds.filter((segmentId) => !(segmentId in contactsCount.value));

        if (!segmentIds.length) {
            return;
        }

        if (fetching.value) {
            // when its currently making a request call wait a bit before retrieving again
            setTimeout(() => {
                fetchSegmentsContactsCount(context, segmentIds, params);
            }, 500);
            return;
        }

        try {
            fetching.value = true;

            const responses = await Promise.all(
                chunkArray(segmentIds, IDS_PER_REQUEST).map((batch) =>
                    segmentsClient.getSegmentsContactsCount(context, {
                        segment_ids: batch,
                        ...params,
                    })
                )
            );

            const counts: Record<number, number> = Object.assign(
                {},
                ...responses.map((response) => response.data.counts)
            );

            segmentIds.forEach((segmentId) => {
                contactsCount.value[segmentId] = counts[segmentId] ?? 0;
            });
        } finally {
            fetching.value = false;
        }
    };

    return {
        fetchSegmentsContactsCount,
        contactsCount,
    };
});
